import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { appsApi, errMsg } from "@/lib/appsApi";
import { useNoIndex } from "@/lib/useNoIndex";
import { AppsSignIn } from "@/components/apps/AppsSignIn";
import { AppsReset } from "@/components/apps/AppsReset";
import { AppsPortal } from "@/components/apps/AppsPortal";

export default function AppsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [error, setError] = useState("");
  const processed = useRef(false);
  useNoIndex();
  const sessionId = new URLSearchParams((location.hash || "").replace(/^#/, "")).get("session_id");
  const resetToken = new URLSearchParams(location.search).get("reset");

  useEffect(() => {
    document.title = "App Testing | Aero Soft Solutions";
  }, []);

  useEffect(() => {
    if (sessionId) {
      if (processed.current) return;
      processed.current = true;
      appsApi.post("/auth/google", { session_id: sessionId })
        .then((r) => setUser(r?.data ?? null))
        .catch((err) => { setError(errMsg(err)); setUser(null); })
        .finally(() => navigate("/apps", { replace: true }));
      return;
    }
    if (resetToken || processed.current) {
      if (resetToken) setUser(null);
      return;
    }
    appsApi.get("/auth/me").then((r) => setUser(r?.data ?? null)).catch(() => setUser(null));
  }, [sessionId, resetToken, navigate]);

  const signOut = async () => {
    await appsApi.post("/auth/logout").catch(() => null);
    setUser(null);
  };

  const resetDone = (msg) => {
    setError("");
    navigate("/apps", { replace: true });
    setUser(null);
    setTimeout(() => setError(msg), 0);
  };

  const card = "mx-auto max-w-md rounded-[32px] bg-white/90 p-8 shadow-[0_40px_100px_-60px_rgba(11,79,138,0.6)] backdrop-blur-xl sm:p-10";
  let body;
  if (user === undefined || sessionId) body = <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  else if (resetToken) body = <div className={card}><AppsReset token={resetToken} onDone={resetDone} /></div>;
  else if (!user) body = <div className={card}><AppsSignIn key={error} onSignedIn={setUser} initialError={error} /></div>;
  else body = <AppsPortal user={user} onSignOut={signOut} />;

  return (
    <div className="sky-wash min-h-screen" data-testid="apps-page">
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-6">
        <Logo />
        <p className="mb-10 mt-12 font-mono text-xs uppercase tracking-[0.25em] text-sky-700">App testing</p>
        {body}
      </div>
    </div>
  );
}
